/**
 * LagRegistroChip — indicador de "días entre pago y registro de la reserva".
 *
 * Mide qué tan oportuno fue el comercial al capturar la reserva en el sistema
 * después de que el cliente pagó. La fuente confiable es la fecha extraída del
 * voucher por Vision; si no hay, cae a fecha_pago declarada del pago más
 * antiguo. Si no hay ninguna, no se pinta chip (aplica=false).
 *
 * Semáforo (acordado con el usuario):
 *   0-1d verde · 2-3d amarillo · >3d rojo
 *
 * Justificación: admin/superadmin/administrativo pueden justificar el lag
 * escribiendo el motivo (agencia envió voucher tarde, etc.). Cuando hay
 * justificación, el chip pasa a neutro pero sigue mostrando el número real.
 *
 * Backend: campo `lag_registro` del endpoint /turismo/reservaciones/{id}.
 * Endpoint de justificación: PATCH /turismo/reservaciones/{id}/justificar-lag.
 */
import React, { useState } from 'react';
import {
  Chip, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Typography, Box, IconButton, CircularProgress
} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EditIcon from '@mui/icons-material/Edit';
import { API_BASE, getAuthHeaders } from './format';

// Colores del semáforo — coherentes con la paleta ya usada en el sistema.
// Etiquetas prefijadas con "Lag" para evitar confusión con el chip de estado
// de pago (que también usa "Al día" y comparte gama verde).
const PALETA = {
  verde:       { bg: '#dcfce7', color: '#15803d', border: '#86efac', label: 'oportuna' },
  amarillo:    { bg: '#fef3c7', color: '#a16207', border: '#fde68a', label: 'rezago' },
  rojo:        { bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5', label: 'retraso' },
  justificado: { bg: '#e0e7ff', color: '#3730a3', border: '#c7d2fe', label: 'justificado' },
};

function fmtFecha(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function _fuenteLabel(f) {
  if (f === 'voucher_vision') return 'Vision · voucher';
  if (f === 'voucher_subido') return 'Voucher subido (sin fecha OCR)';
  return f || '';
}

export default function LagRegistroChip({ reservaId, lagRegistro, puedeJustificar, onUpdated }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [texto, setTexto] = useState(lagRegistro?.justificacion || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!lagRegistro || !lagRegistro.aplica) return null;

  const semaforo = lagRegistro.semaforo || 'verde';
  const paleta = PALETA[semaforo] || PALETA.verde;
  const dias = lagRegistro.dias ?? 0;
  // Prefijamos "Lag" para dejar clarísimo el dominio (no es un estado de pago).
  const chipLabel = `Lag ${dias}d · ${paleta.label}`;

  const tooltip = (
    <Box sx={{ p: 0.5 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>
        Lag entre pago y registro: {dias} {dias === 1 ? 'día' : 'días'}
      </Typography>
      <Typography sx={{ fontSize: 11, opacity: 0.9 }}>
        Voucher/pago: {fmtFecha(lagRegistro.fecha_referencia)}
      </Typography>
      <Typography sx={{ fontSize: 11, opacity: 0.9 }}>
        Registrada: {fmtFecha(lagRegistro.fecha_registro)}
      </Typography>
      <Typography sx={{ fontSize: 10, opacity: 0.7, mt: 0.5, fontStyle: 'italic' }}>
        Fuente: {_fuenteLabel(lagRegistro.fuente)}
      </Typography>
      {lagRegistro.justificacion && (
        <Typography sx={{ fontSize: 11, mt: 0.75, borderTop: '1px solid rgba(255,255,255,0.2)',
                          pt: 0.5, fontStyle: 'italic' }}>
          Justificado: “{lagRegistro.justificacion}”
        </Typography>
      )}
      {puedeJustificar && (
        <Typography sx={{ fontSize: 10, mt: 0.5, opacity: 0.75 }}>
          Click para {lagRegistro.justificacion ? 'editar' : 'justificar'}
        </Typography>
      )}
    </Box>
  );

  const guardar = async () => {
    setSaving(true); setError(null);
    try {
      const resp = await fetch(
        `${API_BASE}/turismo/reservaciones/${reservaId}/justificar-lag`,
        { method: 'PATCH', headers: getAuthHeaders(),
          body: JSON.stringify({ justificacion: texto }) }
      );
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d.detail || `Error ${resp.status}`);
      }
      setDialogOpen(false);
      onUpdated?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Tooltip arrow title={tooltip}>
        <Chip
          size="small"
          icon={<ScheduleIcon sx={{ fontSize: '14px !important',
                                     color: `${paleta.color} !important` }} />}
          label={chipLabel}
          onClick={puedeJustificar ? () => { setTexto(lagRegistro.justificacion || ''); setDialogOpen(true); } : undefined}
          sx={{
            bgcolor: paleta.bg, color: paleta.color,
            fontWeight: 800, fontSize: 11, height: 22,
            border: `1px solid ${paleta.border}`,
            cursor: puedeJustificar ? 'pointer' : 'default',
            '& .MuiChip-icon': { ml: 0.5 },
            '& .MuiChip-label': { pl: 0.5 },
          }}
        />
      </Tooltip>

      {puedeJustificar && (
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>
            {lagRegistro.justificacion ? 'Editar justificación de lag' : 'Justificar lag de registro'}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 13, color: '#475569', mb: 2 }}>
              Esta reserva se registró <b>{dias} {dias === 1 ? 'día' : 'días'}</b> después
              del voucher/pago. Explica por qué no fue registrada a tiempo — la reserva
              seguirá visible pero contará como neutra (no rojo) en KPIs.
            </Typography>
            <TextField
              autoFocus multiline rows={3} fullWidth
              label="Justificación"
              placeholder="Ej: La agencia envió el voucher 5 días tarde por error de su asistente."
              value={texto}
              onChange={(e) => setTexto(e.target.value.slice(0, 500))}
              helperText={`${texto.length}/500 · Deja vacío para eliminar la justificación`}
            />
            {error && (
              <Typography sx={{ mt: 1, color: '#dc2626', fontSize: 12 }}>
                {error}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}
                    sx={{ textTransform: 'none' }}>
              Cancelar
            </Button>
            <Button onClick={guardar} disabled={saving} variant="contained" disableElevation
                    sx={{ textTransform: 'none', fontWeight: 700,
                          background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
              {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> :
               (texto.trim() ? 'Guardar justificación' : 'Eliminar justificación')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
